import type { Vec2 } from './types.js';

/**
 * Real-world Atlantic surface currents, simplified into elliptical zones with
 * a constant flow vector. The ship picks up `(directionRad, speedKnots)` for
 * each zone whose ellipse it sits inside, scaled by `1 - distFromCenter/maxR`
 * so the strongest pull is at the heart of the stream and tapers out.
 *
 * Bearings use the same convention as everything else: 0 = East, π/2 = South,
 * π = West, 3π/2 = North.
 *
 * Speed in knots. A 2-knot current applied for one hour moves a ship ~3.7 km;
 * over a 30-day voyage that's a few hundred km of free push (or drag).
 */
export interface CurrentZone {
  name: string;
  /** Center of the zone in world coordinates. */
  center: Vec2;
  /** Major / minor semi-axes (km) of the ellipse. */
  rxKm: number;
  ryKm: number;
  /** Ellipse rotation (radians, CCW). 0 = major axis along +X. */
  rotationRad: number;
  /** Flow direction in radians (game convention). */
  directionRad: number;
  /** Flow speed at the zone's center, in knots. Tapers linearly with distance. */
  speedKnots: number;
}

export const CURRENTS: CurrentZone[] = [
  {
    // North Equatorial Current — wide W-flowing band carrying ships from the
    // African coast toward the Caribbean. The current that brought Columbus.
    // Roughly 12°-25°N, broad. In our coordinates, y ≈ 1300–2750.
    name: 'North Equatorial Current',
    center: { x: -3500, y: 2050 },
    rxKm: 4200,
    ryKm: 700,
    rotationRad: 0,
    directionRad: Math.PI,            // due west
    speedKnots: 1.2,
  },
  {
    // Gulf Stream — strong NE-flowing current along the SE coast of North
    // America, then bending east as the North Atlantic Drift. Helps eastbound
    // voyages, opposes anyone hugging the Florida → Carolinas seaboard.
    name: 'Gulf Stream',
    center: { x: -5800, y: 100 },
    rxKm: 2400,
    ryKm: 350,
    rotationRad: -Math.PI / 6,        // tilted ENE → WSW
    directionRad: -Math.PI / 6,       // flowing ENE (≈ 0.52 rad north of east)
    speedKnots: 2.0,
  },
  {
    // Canary Current — cool S-flowing current down the West African coast.
    // A free push for ships running from the Canaries to Cape Verde.
    name: 'Canary Current',
    center: { x: -650, y: 1500 },
    rxKm: 380,
    ryKm: 1300,
    rotationRad: 0,
    directionRad: Math.PI / 2,        // due south
    speedKnots: 1.0,
  },
  {
    // North Atlantic Drift — eastward continuation of the Gulf Stream toward
    // Europe. Light push for any ship returning to Iberia / France / Britain.
    name: 'North Atlantic Drift',
    center: { x: -2000, y: -1100 },
    rxKm: 2500,
    ryKm: 600,
    rotationRad: 0,
    directionRad: 0,                  // due east
    speedKnots: 0.8,
  },
];

/**
 * Sum every current zone's flow at a position into a single (vx, vy) in knots.
 * Returns {0, 0} if the point is in open ocean with no current.
 */
export function currentAt(p: Vec2): { vx: number; vy: number } {
  let vx = 0, vy = 0;
  for (const c of CURRENTS) {
    // Translate + rotate the point into the ellipse's local frame
    const dx = p.x - c.center.x;
    const dy = p.y - c.center.y;
    const cos = Math.cos(-c.rotationRad);
    const sin = Math.sin(-c.rotationRad);
    const lx = dx * cos - dy * sin;
    const ly = dx * sin + dy * cos;
    // Normalized distance from center inside the ellipse
    const t = Math.hypot(lx / c.rxKm, ly / c.ryKm);
    if (t >= 1) continue; // outside the ellipse
    const strength = c.speedKnots * (1 - t);
    vx += Math.cos(c.directionRad) * strength;
    vy += Math.sin(c.directionRad) * strength;
  }
  return { vx, vy };
}
